import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import LandingHeader from '../Shared/LandingHeader';
import LandingFooter from '../Shared/LandingFooter';
import './LandingPage.css';

const FALLBACK_TESTIMONIALS = [
  { id: 1, name: 'Sarah J.', role: 'Parent of Grade 8 Student', quote: 'SkillTherapy AI changed how my son approaches math. He actually enjoys the 10-min practice runs!', avatar: '/testimonial_avatars_1771938882278.png', objectPos: 'top center' },
  { id: 2, name: 'David W.', role: 'Grade 10 Student', quote: 'The AI identified exactly what I was missing in Physics. SkillTherapy AI helped my scores go up by 20% in a month.', avatar: '/testimonial_avatars_1771938882278.png', objectPos: 'center bottom' },
  { id: 3, name: 'Linda K.', role: 'Parent of Grade 6 Student', quote: 'The visual reports are so easy to understand. I finally know how to help my daughter.', avatar: '/testimonial_avatars_1771938882278.png', objectPos: 'right top' },
];

const LandingPage = () => {
  const { t } = useTranslation(['landing', 'common']);
  const navigate = useNavigate();

  const [testimonials, setTestimonials] = useState([]);
  const [testimonialsLoading, setTestimonialsLoading] = useState(true);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState({ type: '', message: '' });
  const [isSubmittingNewsletter, setIsSubmittingNewsletter] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;

    setIsSubmittingNewsletter(true);
    setNewsletterStatus({ type: '', message: '' });

    try {
      const response = await fetch(`${API_BASE_URL}subscribe_newsletter.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newsletterEmail }),
        credentials: 'include'
      });
      const data = await response.json();

      setNewsletterStatus({ type: data.status, message: data.message });
      if (data.status === 'success' || data.status === 'info') {
        setNewsletterEmail('');
      }
    } catch (error) {
      setNewsletterStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setIsSubmittingNewsletter(false);
    }
  };

  useEffect(() => {
    if (localStorage.getItem('userId')) {
      navigate('/dashboard');
    }
  }, [navigate]);

  useEffect(() => {
    fetch(`${API_BASE_URL}testimonials.php`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (data.status === 'success' && data.data.length > 0) {
          setTestimonials(data.data);
        } else {
          setTestimonials(FALLBACK_TESTIMONIALS);
        }
      })
      .catch(() => setTestimonials(FALLBACK_TESTIMONIALS))
      .finally(() => setTestimonialsLoading(false));
  }, []);

  return (
    <div className="landing-page">
      <LandingHeader activePage="home" />

      <main>
        {/* Hero Section */}
        <section className="hero" id="home">
          <div className="container hero-container">
            <div className="hero-content">
              <h1 className="hero-title">
                <span className="gradient-text">{t('landing:hero.titlePrefix')}</span> {t('landing:hero.title')}
              </h1>

              <p className="hero-subtext">
                {t('landing:hero.subtext')}
              </p>

              <div className="hero-btns">
                <Link to="/register" className="btn btn-primary">{t('landing:hero.getStarted')}</Link>
                <Link to="/demo" className="btn btn-secondary">{t('landing:hero.watchDemo')}</Link>
              </div>

            </div>
            <div className="hero-visual">
              <img
                src="/hero_student_3d_1771938273119.png"
                alt="Futuristic Learning"
                className="hero-image"
              />
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="features" id="features">
          <div className="container">
            <h2 className="section-title">{t('landing:features.title')}</h2>

            <div className="features-grid">
              <div className="feature-card">
                <div className="icon-wrapper">
                  <img src="/ai_diagnostic_icon.png" alt="AI Test" className="feature-icon" />
                </div>
                <h3>{t('landing:features.diagnostic.title')}</h3>
                <p>{t('landing:features.diagnostic.text')}</p>
              </div>
              <div className="feature-card">
                <div className="icon-wrapper">
                  <img src="/study_plan_icon.png" alt="Study Plan" className="feature-icon" />
                </div>
                <h3>{t('landing:features.studyPlan.title')}</h3>
                <p>{t('landing:features.studyPlan.text')}</p>
              </div>
              <div className="feature-card">
                <div className="icon-wrapper">
                  <img src="/micro_learning_icon.png" alt="Practice" className="feature-icon" />
                </div>
                <h3>{t('landing:features.microLearning.title')}</h3>
                <p>{t('landing:features.microLearning.text')}</p>
              </div>
              <div className="feature-card">
                <div className="icon-wrapper">
                  <img src="/exam_readiness_icon.png" alt="Readiness" className="feature-icon" />
                </div>
                <h3>{t('landing:features.examReadiness.title')}</h3>
                <p>{t('landing:features.examReadiness.text')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Android App Section */}
        <section className="app-download" id="app">
          <div className="container app-container">
            <div className="app-content">
              <h2 className="section-title" style={{ textAlign: 'left', marginBottom: '1.5rem' }}>
                {t('landing:app.title')}
              </h2>
              <p className="app-description">
                {t('landing:app.description')}
              </p>

              <ul className="app-features">
                <li><span className="check">✓</span> {t('landing:app.feature1')}</li>
                <li><span className="check">✓</span> {t('landing:app.feature2')}</li>
                <li><span className="check">✓</span> {t('landing:app.feature3')}</li>
                <li><span className="check">✓</span> {t('landing:app.feature4')}</li>
                <li><span className="check">✓</span> {t('landing:app.feature5')}</li>
                <li><span className="check">✓</span> {t('landing:app.feature6')}</li>
              </ul>
              <div className="app-btns">
                <a 
                  href="https://play.google.com/store/apps/details?id=com.skilltherapy.ai" 
                  className="btn btn-primary android-btn"
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <img src="/google_play_store_badge.svg" alt="Get it on Google Play" style={{ height: '40px' }} />
                </a>
              </div>
            </div>
            <div className="app-visual">
              <div className="phone-mockup">
                <img
                  src="/android_app_mockup_1773576220262.png"
                  alt="SkillTherapy AI Android App"
                  className="phone-image"
                />
                <div className="phone-glow"></div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="how-it-works">
          <div className="container">
            <h2 className="section-title">{t('landing:howItWorks.title')}</h2>

            <div className="steps-container">
              <div className="step-card">
                <div className="step-number">1</div>
                <h3>{t('landing:howItWorks.step1.title')}</h3>
                <p>{t('landing:howItWorks.step1.text')}</p>
              </div>
              <div className="step-card">
                <div className="step-number" style={{ background: 'linear-gradient(45deg, var(--secondary-neon), var(--primary-neon))' }}>2</div>
                <h3>{t('landing:howItWorks.step2.title')}</h3>
                <p>{t('landing:howItWorks.step2.text')}</p>
              </div>
              <div className="step-card">
                <div className="step-number">3</div>
                <h3>{t('landing:howItWorks.step3.title')}</h3>
                <p>{t('landing:howItWorks.step3.text')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="testimonials">
          <div className="container">
            <h2 className="section-title">
              {t('landing:testimonials.titlePrefix')}
              <span className="gradient-text">{t('landing:testimonials.parents')}</span>
              {t('landing:testimonials.titleSuffix')}
            </h2>

            <div className="testimonial-carousel">
              {testimonialsLoading ? (
                [1, 2, 3].map(i => (
                  <div key={i} className="testimonial-card testimonial-skeleton">
                    <div className="skeleton-avatar" />
                    <div className="skeleton-line" style={{ width: '60%' }} />
                    <div className="skeleton-line" style={{ width: '40%' }} />
                    <div className="skeleton-line" style={{ width: '100%', marginTop: '1rem' }} />
                    <div className="skeleton-line" style={{ width: '80%' }} />
                  </div>
                ))
              ) : (
                testimonials.map(t => (
                  <div key={t.id} className="testimonial-card">
                    <p style={{ margin: '0.75rem 0 1.25rem', flex: 1 }}>"{t.quote}"</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="avatar"
                        style={{ objectFit: 'cover', objectPosition: t.objectPos }}
                      />
                      <div>
                        <h4 style={{ margin: 0 }}>{t.name}</h4>
                        <p style={{ fontSize: '0.8rem', color: 'var(--text-gray)', margin: 0 }}>{t.role}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="cta-section container">
          <h2 style={{ fontSize: '3rem', marginBottom: '1.5rem' }}>{t('landing:hero.title')}</h2>
          <p style={{ marginBottom: '2.5rem', color: 'var(--text-gray)' }}>{t('landing:hero.subtext')}</p>
          <Link to="/register" className="btn btn-primary" style={{ fontSize: '1.2rem' }}>{t('landing:hero.getStarted')}</Link>
        </section>

      </main>

      <LandingFooter />
    </div>
  );
};

export default LandingPage;
